import { type RefObject, useEffect, useRef, useState } from "react";

/**
 * В timeGrid-видах высота тела сетки — это `слоты × slotHeight`, от коробки она не зависит
 * (§3 docs/frontend-architecture.md). Этот хук замеряет коробку и считает slotHeight так,
 * чтобы сетка заняла её целиком: ни «подбородка», ни внутреннего скролла. Когда слот
 * получился бы нечитаемо низким, сначала сжимается диапазон часов.
 */

const HOUR_RANGES = [
  { slotMinTime: "06:00", slotMaxTime: "22:00", slots: 32 },
  { slotMinTime: "07:00", slotMaxTime: "21:00", slots: 28 },
  { slotMinTime: "08:00", slotMaxTime: "20:00", slots: 24 },
] as const;

/** Ниже двух строк текста карточка урока перестаёт читаться. */
const MIN_READABLE_SLOT = 16;

export interface CalendarFit {
  readonly slotHeight: number;
  readonly slotMinTime: string;
  readonly slotMaxTime: string;
}

const DEFAULT_FIT: CalendarFit = {
  slotHeight: 24,
  slotMinTime: HOUR_RANGES[0].slotMinTime,
  slotMaxTime: HOUR_RANGES[0].slotMaxTime,
};

function computeFit(boxHeight: number, headerHeight: number): CalendarFit {
  // 2px — собственная рамка .ec: box-sizing не отдаёт её содержимому.
  const available = boxHeight - headerHeight - 2;
  for (const range of HOUR_RANGES) {
    const slotHeight = available / range.slots;
    if (slotHeight >= MIN_READABLE_SLOT) {
      return { slotHeight, slotMinTime: range.slotMinTime, slotMaxTime: range.slotMaxTime };
    }
  }
  const narrowest = HOUR_RANGES[HOUR_RANGES.length - 1] ?? HOUR_RANGES[0];
  return {
    slotHeight: MIN_READABLE_SLOT,
    slotMinTime: narrowest.slotMinTime,
    slotMaxTime: narrowest.slotMaxTime,
  };
}

export function useCalendarFit(viewKey: string): {
  boxRef: RefObject<HTMLDivElement | null>;
  fit: CalendarFit;
} {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [fit, setFit] = useState<CalendarFit>(DEFAULT_FIT);
  const computeRef = useRef<() => void>(() => {});

  computeRef.current = () => {
    const box = boxRef.current;
    if (!box) return;
    const boxHeight = box.clientHeight;
    if (boxHeight <= 0) return;
    const headerHeight = box.querySelector<HTMLElement>(".ec-header")?.offsetHeight ?? 40;
    const next = computeFit(boxHeight, headerHeight);
    setFit((prev) =>
      Math.abs(prev.slotHeight - next.slotHeight) < 0.5 &&
      prev.slotMinTime === next.slotMinTime &&
      prev.slotMaxTime === next.slotMaxTime
        ? prev
        : next,
    );
  };

  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === "undefined") {
      return;
    }
    computeRef.current();
    const observer = new ResizeObserver(() => computeRef.current());
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  // Смена вида меняет высоту заголовка сетки, а коробку — нет: пересчитываем после отрисовки.
  useEffect(() => {
    // viewKey нужен только как триггер: сам пересчёт читает DOM.
    void viewKey;
    if (typeof requestAnimationFrame === "undefined") {
      return;
    }
    const frame = requestAnimationFrame(() => computeRef.current());
    return () => cancelAnimationFrame(frame);
  }, [viewKey]);

  return { boxRef, fit };
}
